import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';

const UserManager = () => {
  const { user: currentAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableError, setTableError] = useState('');
  const [success, setSuccess] = useState('');

  // New user form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [creating, setCreating] = useState(false);

  // Delete dialog state
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setTableError('');
      const res = await api.get('/auth/users');
      if (Array.isArray(res.data)) {
        setUsers(res.data);
      } else if (res.data && Array.isArray(res.data.users)) {
        setUsers(res.data.users);
      } else {
        setUsers([]);
        if (res.data && res.data.message) {
          setTableError(res.data.message);
        } else {
          setTableError('Unexpected response from server: ' + JSON.stringify(res.data));
        }
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      setUsers([]);
      const errMsg = err.response?.data?.message || err.message || 'Failed to fetch users list.';
      setTableError(`${errMsg} (${err.response?.status || 'Network Error'})`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please provide both email and password.');
      return;
    }
    if (password.length < 6) {
      setError('Password should be at least 6 characters long.');
      return;
    }

    try {
      setCreating(true);
      setError('');
      setSuccess('');
      const res = await api.post('/auth/users', {
        email: email.trim(),
        password,
        role,
      });
      setSuccess(res.data?.message || 'User created successfully!');
      setEmail('');
      setPassword('');
      setRole('admin');
      fetchUsers();
    } catch (err) {
      console.error('Create user error:', err);
      setError(err.response?.data?.message || 'Failed to create user.');
    } finally {
      setCreating(false);
    }
  };

  const openDeleteDialog = (targetUser) => {
    setUserToDelete(targetUser);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteDialog = () => {
    setUserToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setDeleting(true);
      await api.delete(`/auth/users/${userToDelete.id}`);
      setSuccess(`User ${userToDelete.email} was deleted successfully.`);
      closeDeleteDialog();
      fetchUsers();
    } catch (err) {
      console.error('Delete user error:', err);
      setError(err.response?.data?.message || 'Failed to delete user.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* Create User Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="primary" /> Create New Administrator or User
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Public signups are disabled. Only existing administrators can create other admin accounts. (Reviewers are automatically registered when they access a review link).
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Box component="form" onSubmit={handleCreateUser}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              required
              label="Email Address"
              type="email"
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ minWidth: 260, flexGrow: 1 }}
            />
            <TextField
              required
              label="Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Minimum 6 characters"
              sx={{ minWidth: 220, flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="user-role-label">Role</InputLabel>
              <Select
                labelId="user-role-label"
                value={role}
                label="Role"
                onChange={(e) => setRole(e.target.value)}
              >
                <MenuItem value="admin">Administrator (Dashboard Access)</MenuItem>
                <MenuItem value="user">Reviewer / Standard User</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
          >
            {creating ? 'Creating...' : role === 'admin' ? 'Create Administrator' : 'Create User'}
          </Button>
        </Box>
      </Paper>

      {/* Users List Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            System Users & Reviewers
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {tableError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {tableError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (!Array.isArray(users) || users.length === 0) ? (
          <Typography color="text.secondary">No users found.</Typography>
        ) : (
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Access Type</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => {
                  const isCurrent = currentAdmin && currentAdmin.id === u.id;
                  const isAdminRole = u.role === 'admin';
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: isCurrent ? 'bold' : 'normal' }}>
                          {u.email} {isCurrent && '(You)'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isAdminRole ? 'ADMIN' : 'REVIEWER'}
                          size="small"
                          color={isAdminRole ? 'primary' : 'default'}
                          variant={isAdminRole ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isAdminRole ? 'Admin Dashboard' : 'Review Links Only'}
                          size="small"
                          color={isAdminRole ? 'primary' : 'info'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {u.created_at ? new Date(u.created_at).toLocaleString() : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          edge="end"
                          color="error"
                          aria-label="delete user"
                          disabled={isCurrent}
                          title={isCurrent ? "You cannot delete your own account" : "Delete user"}
                          onClick={() => openDeleteDialog(u)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user <strong>{userToDelete?.email}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManager;
